import memoizeOne from 'memoize-one';
import React, { Component, Fragment, ReactNode } from 'react';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { CaLabeling as ApiCaLabeling, Product, ProductUpdate } from '~/Api/User/ProductGroup/CaLabeling';
import ProductsTable, { ProductRow, ProductValues } from '~/CaLabeling/ProductsTable/ProductsTable';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import { parsePackageSize, PricedProduct, Product as GeneralProduct } from '~/Product';
import ProductDetailModal from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Heading from '~/UI/Heading/Heading';

import { ProductProvider } from './ProductProvider';
import styles from './styles.less';

interface Props {
	canadianProducts: ProductRow[];
	currency: string | null;
	priceType: string | null;
	filterCalculatedPrice: boolean;
	detailProductId: string | null;
	detailProductTabName: string;
	group: UserProductGroup;
	header: ReactNode;
	caLabeling: ApiCaLabeling | null;
	isFetching: boolean;
	otherProducts: ProductRow[];
	pricedProducts: Array<PricedProduct<Product>>;
	referenceBasketVersion: ReferenceBasketVersion | null;
	referencedProducts: ProductRow[];
	isFetchingProductPrices: boolean;
	onCloseDetail(): void;
	onDeleteItems(group: UserProductGroup, product: GeneralProduct[], onSuccess: () => void): Promise<void>;
	onFetch(group: UserProductGroup, currency: string | null): Promise<void>;
	onPrune(group: UserProductGroup): void;
	onSave(group: UserProductGroup): Promise<void>;
	onShowDetail(productId: string | null, tabName: string): void;
	onUpdate(group: UserProductGroup, basketVersion: string | null, products: ProductUpdate[]): void;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onPriceChanged(currency: string | null, priceType: string | null, filterCalculatedPrice: boolean): void;
	onCurrencyChanged(
		group: UserProductGroup,
		currency: string | null,
		pricedProducts: Array<PricedProduct<Product>>
	): void;
}

interface Values {
	product: { [productId: string]: ProductValues };
}

interface State {
	lastSeenProductIdDetail: string | null;
	removeProduct: GeneralProduct | null;
	values: Values | null;
	checkedRows: GeneralProduct[];
}

export default class CaLabeling extends Component<Props, State> {
	public static defaultProps = {
		detailProductId: null,
		detailProductTabName: 'info',
		caLabeling: null,
	};

	public state: State = {
		lastSeenProductIdDetail: null,
		removeProduct: null,
		values: null,
		checkedRows: [],
	};

	private readonly checkRow = (product: GeneralProduct, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	private readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: GeneralProduct[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	private readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	private readonly createValues = memoizeOne(
		(caLabeling: ApiCaLabeling | null, values: Values | null): Values => ({
			product:
				caLabeling?.products.reduce((products: { [productId: string]: ProductValues }, product) => {
					const currentValues = values?.product[product.product.id];
					if (currentValues !== undefined) {
						products[product.product.id] = currentValues;
						return products;
					}

					products[product.product.id] = {
						packageSize: {
							isValid: true,
							value: product.packageSizeForCalculation !== null ? String(product.packageSizeForCalculation) : '',
						},
					};
					return products;
				}, {}) ?? {},
		})
	);

	public componentDidMount() {
		const { group, currency, onFetch } = this.props;
		onFetch(group, currency);
	}

	public componentDidUpdate(prevProps: Props) {
		const { group, currency, pricedProducts, onFetch, onCurrencyChanged } = this.props;

		if (prevProps.group !== group || prevProps.group.exchangeRates !== group.exchangeRates) {
			onFetch(group, currency);
		} else if (prevProps.currency !== currency && pricedProducts.length > 0) {
			onCurrencyChanged(group, currency, pricedProducts);
		}
	}

	public componentWillUnmount() {
		const { group, onPrune } = this.props;
		onPrune(group);
	}

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	public render() {
		const {
			props: {
				header,
				caLabeling,
				group,
				pricedProducts,
				canadianProducts,
				referencedProducts,
				otherProducts,
				isFetching,
				detailProductId,
				productDetailLink,
				detailProductTabName,
				currency,
				priceType,
				filterCalculatedPrice,
				isFetchingProductPrices,
				onShowDetail,
				onSave,
				onPriceChanged,
			},
			state: { lastSeenProductIdDetail, removeProduct, checkedRows },
		} = this;

		const values = this.resolveValues();

		if (caLabeling === null && isFetching) {
			return <ActivityIndicator />;
		}

		return (
			<Fragment>
				{header}

				<section className={styles.section}>
					{detailProductId !== null ? (
						<ProductProvider caLabeling={caLabeling} id={detailProductId}>
							{(product) => (
								<ProductDetailModal
									currency={currency}
									groupId={group.id}
									isFetching={false}
									isFetchingPrices={isFetchingProductPrices}
									product={product}
									productId={detailProductId}
									products={pricedProducts}
									selectedTabName={detailProductTabName}
									shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
										detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
									}`}
									onClose={this.handleCloseDetail}
									onCurrencyChange={(currency) => onPriceChanged(currency, priceType, filterCalculatedPrice)}
									onChange={(productId) => {
										onShowDetail(productId, detailProductTabName);
									}}
									onSelectTab={(tabName) => {
										onShowDetail(detailProductId, tabName);
									}}
								/>
							)}
						</ProductProvider>
					) : null}

					{pricedProducts.length === 0 ? <ProductInstructions /> : null}

					{pricedProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>Canadian products</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 ? (
						<ProductsTable
							disablePackageSize
							currencies={group.exchangeRates.availableCurrencies}
							currency={currency}
							detailLink={productDetailLink}
							filterCalculatedPrice={filterCalculatedPrice}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							isFetching={isFetchingProductPrices}
							priceType={priceType}
							products={canadianProducts}
							productValues={values.product}
							onChange={this.productValuesChanged}
							onPriceChanged={onPriceChanged}
							onSave={() => {
								onSave(group);
							}}
						/>
					) : null}

					{pricedProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<Heading level={2}>Reference basket products</Heading>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 ? (
						<ProductsTable
							currencies={group.exchangeRates.availableCurrencies}
							currency={currency}
							detailLink={productDetailLink}
							filterCalculatedPrice={filterCalculatedPrice}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							isFetching={isFetchingProductPrices}
							priceType={priceType}
							products={referencedProducts}
							productValues={values.product}
							onChange={this.productValuesChanged}
							onPriceChanged={onPriceChanged}
							onSave={() => {
								onSave(group);
							}}
						/>
					) : null}

					{pricedProducts.length > 0 && otherProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>Other</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 && otherProducts.length > 0 ? (
						<ProductsTable
							currencies={group.exchangeRates.availableCurrencies}
							currency={currency}
							detailLink={productDetailLink}
							filterCalculatedPrice={filterCalculatedPrice}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							isFetching={isFetchingProductPrices}
							priceType={priceType}
							products={otherProducts}
							productValues={values.product}
							onChange={this.productValuesChanged}
							onPriceChanged={onPriceChanged}
							onSave={() => {
								onSave(group);
							}}
						/>
					) : null}

					{removeProduct !== null ? (
						<ProductGroupRemoveProductModal
							group={group}
							product={removeProduct}
							onClose={() => {
								this.setState({ removeProduct: null });
							}}
							onConfirm={async () => {
								await onSave(group);
							}}
							onSuccess={() => {
								this.setState({ removeProduct: null });
							}}
						/>
					) : null}
					{checkedRows.length > 0 ? (
						<CopyGroupProductActionPanel
							group={group}
							products={checkedRows}
							selectedProductsCount={this.countRows()}
							onClickCancelDelete={this.onClickCancelDelete}
							onClickRemoveRow={this.onDeleteItems}
							onProductsCopied={this.handleProductsCopied}
						/>
					) : null}
				</section>
			</Fragment>
		);
	}

	private readonly handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};

	private readonly productValuesChanged = (item: Product, packageSize: string) => {
		const values = this.resolveValues();

		this.setState(
			{
				values: {
					product: {
						...values.product,
						[item.product.id]: {
							packageSize: { value: packageSize, isValid: null },
						},
					},
				},
			},
			() => {
				this.update();
			}
		);
	};

	private resolveValues(): Values {
		const {
			props: { caLabeling },
			state: { values },
		} = this;
		return this.createValues(caLabeling, values);
	}

	private readonly update = () => {
		const values = this.resolveValues();
		const productValues = { ...values.product };
		let hasValidationError = false;

		const products: ProductUpdate[] = [];

		Object.keys(productValues).forEach((productId) => {
			const values = productValues[productId];
			const { packageSize } = values;

			const parsedPackageSize = parsePackageSize(packageSize.value);

			hasValidationError = hasValidationError || parsedPackageSize.isValid === false;

			productValues[productId] = {
				...values,
				packageSize: {
					...values.packageSize,
					isValid: parsedPackageSize.isValid,
				},
			};

			products.push(new ProductUpdate(productId, parsedPackageSize.value));
		});

		this.setState({ values: { product: productValues } });

		if (!hasValidationError && this.props.caLabeling !== null) {
			this.props.onUpdate(this.props.group, this.props.caLabeling?.reference.referenceBasketVersion, products);
		}
	};
}
